import AchievementsStep from '@/components/v2/steps/AchievementsStep'
import LanguageGridStep from '@/components/v2/steps/LanguageGridStep'
import LessonStep from '@/components/v2/steps/LessonStep'
import LoaderStep from '@/components/v2/steps/LoaderStep'
import MascotStep from '@/components/v2/steps/MascotStep'
import MultiSelectStep from '@/components/v2/steps/MultiSelectStep'
import SingleSelectStep from '@/components/v2/steps/SingleSelectStep'
import { useSelectedLanguageName } from '@/hooks/useSelectedLanguageName'
import {
  DAILY_GOAL_MINUTES,
  DEFAULT_DAILY_GOAL_MINUTES,
  type V2Step,
} from './steps'

/**
 * Renders one entry of the v2 step table. Every screen gets the same
 * interpolation bag, so `{{language}}` and `{{minutes}}` resolve the same way
 * in both the visible question and the English-resolved form field name the
 * onboarding action tracks.
 */
export default function V2OnboardingStep({
  step,
  stepNumber,
  totalSteps,
}: {
  step: V2Step
  /** 1-based position of this screen in the flow. */
  stepNumber: number
  totalSteps: number
}) {
  const language = useSelectedLanguageName()

  const minutes =
    DAILY_GOAL_MINUTES[localStorage.getItem('dailyGoal') ?? ''] ??
    DEFAULT_DAILY_GOAL_MINUTES

  const interpolation = { language, minutes }

  const shared = { interpolation, step: stepNumber, totalSteps }

  const persist = (storageKey: string | undefined) => (value: string) => {
    if (storageKey && value) localStorage.setItem(storageKey, value)
  }

  /*
    Duolingo slides horizontally between questions rather than cutting, which is
    what makes the register flow read as one moving sequence instead of a stack
    of separate pages. Keyed on the path so React remounts the wrapper and the
    animation replays on every navigation.

    Safe to put a transform here because nothing in v2 is `position: fixed` —
    the footers are all `sticky`, which is unaffected by a transformed ancestor.
  */
  return (
    <div className="animate-slide-in-right" key={step.path}>
      {renderStep()}
    </div>
  )

  function renderStep() {
    switch (step.type) {
      case 'language':
        return (
          <LanguageGridStep
            {...shared}
            answers={step.answers}
            onAnswered={persist(step.storageKey)}
            titleKey={step.titleKey}
            withLogin={step.withLogin}
          />
        )
      case 'single-select':
        return (
          <SingleSelectStep
            {...shared}
            answers={step.answers}
            motif={step.motif}
            onAnswered={persist(step.storageKey)}
            subtitleKey={step.subtitleKey}
            titleKey={step.titleKey}
          />
        )
      case 'multi-select':
        return (
          <MultiSelectStep
            {...shared}
            answers={step.answers}
            subtitleKey={step.subtitleKey}
            titleKey={step.titleKey}
          />
        )
      case 'achievements':
        return (
          <AchievementsStep
            {...shared}
            benefits={step.benefits}
            titleKey={step.titleKey}
          />
        )
      case 'mascot':
        return (
          <MascotStep
            {...shared}
            bodyKey={step.bodyKey}
            titleKey={step.titleKey}
          />
        )
      case 'loader':
        return (
          <LoaderStep
            {...shared}
            bodyKey={step.bodyKey}
            durationMs={step.durationMs}
            progressLabelKey={step.progressLabelKey}
            titleKey={step.titleKey}
          />
        )
      case 'lesson':
        return <LessonStep />
      default: {
        const exhaustiveCheck: never = step
        return exhaustiveCheck
      }
    }
  }
}
